import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchSanity, isSanityConfigured, sanityLiveClient } from "@/lib/sanity";

const isLiveSyncEnabled = import.meta.env.VITE_SANITY_LIVE_SYNC !== "false";

export function useSanityQuery<T>(queryKey: unknown[], query: string, params?: Record<string, unknown>) {
  const queryClient = useQueryClient();
  const queryKeyHash = JSON.stringify(queryKey);
  const paramsHash = JSON.stringify(params ?? {});

  const queryResult = useQuery<T>({
    queryKey,
    queryFn: () => fetchSanity<T>(query, params),
    enabled: isSanityConfigured,
  });

  useEffect(() => {
    if (!isSanityConfigured || !isLiveSyncEnabled) return;

    const subscription = sanityLiveClient
      .listen(query, params, { includeResult: true, visibility: "query" })
      .subscribe((update) => {
        if (update.result) {
          queryClient.setQueryData(queryKey, update.result as T);
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [query, queryKeyHash, paramsHash, queryClient]);

  return queryResult;
}
